import { SwitchField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import { ValidationErrorResponse } from '@wyz/types';
import {
  Card,
  FormContainer,
  Icon,
  Row,
  Text,
  Title,
  Tooltip,
  Flex,
} from '@wyz/ui';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useValidationForm } from '~/common/hooks';
import { CommonErrorCodesEnumType } from '~/common/types';
import {
  QuoteServiceCustomServiceTable,
  QuoteServiceGeometryTable,
  useQuoteServicePricingPolicyForm,
} from '~/purchase/components';
import { QuoteServicePricingPolicyHelpers } from '~/purchase/helpers';
import {
  PRICE_TYPE_ENUM,
  PRICING_STATUS_ENUM,
  quoteServiceGeometryStepFormSchema,
} from '~/purchase/schemas';
import {
  IGlobalQuoteServicePricingFormData,
  IPricingStatus,
  IQuoteServiceGeometryStepForm,
} from '~/purchase/types';

type CommonFormProps = {
  globalFormData: IGlobalQuoteServicePricingFormData;
};
type GeometryStepFormProps = CommonFormProps & {
  onSubmit: (
    data: IQuoteServiceGeometryStepForm,
    status: IPricingStatus,
  ) => void;
  errors?: null | ValidationErrorResponse<CommonErrorCodesEnumType>;
};

const GeometryStepForm: React.FC<GeometryStepFormProps> = ({
  onSubmit,
  globalFormData,
  errors,
}) => {
  const { t } = useI18n();
  const methods = useForm<IQuoteServiceGeometryStepForm>({
    defaultValues: globalFormData.GEOMETRY_SERVICE,
    shouldUnregister: true,
    resolver: zodResolver(quoteServiceGeometryStepFormSchema),
  });
  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });

  const pricingPolicyForm = useQuoteServicePricingPolicyForm();
  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData, event) => {
        onSubmit(
          formData,
          ((event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute(
            'data-status',
          ) as IPricingStatus) ?? PRICING_STATUS_ENUM.DRAFT,
        );
      }}
      formConfiguration={{ mode: pricingPolicyForm.mode }}
      id={QuoteServicePricingPolicyHelpers.SERVICE_PRICING_POLICY_STEP_INDEX.GEOMETRY_SERVICE.toString()}
      className='space-y-4'
    >
      <Card>
        <Card.Header>
          <Flex alignItems='center' justifyContent='between'>
            <Card.Title>
              <Title heading='xl' weight='bold'>
                <Trans i18nKey='app.common.service.geometry' />
              </Title>
            </Card.Title>
            <Flex justifyContent='end'>
              <Text heading='xs' weight='semi-bold'>
                <Flex>
                  <span>
                    <Trans i18nKey='app.quote.pricing_policy.form.step1.refine_by' />
                  </span>
                  <Tooltip
                    label={
                      <Trans i18nKey='app.quote.pricing_policy.form.step1.refine_by_helper' />
                    }
                  >
                    <Icon icon='information-line' />
                  </Tooltip>
                </Flex>
              </Text>
              <SwitchField
                name='refinementFlags.tyreType'
                rightLabel={
                  <Trans i18nKey='app.common.product.vehicule_type' />
                }
                leftLabel=''
                variant='DOUBLE'
                size='md'
              />
            </Flex>
          </Flex>
        </Card.Header>
        <Card.Body>
          <Row className='mt-3'>
            {globalFormData.MOUNTING?.vehicleTypeGroup && (
              <QuoteServiceGeometryTable
                selectedVehicleTypeGroup={
                  globalFormData.MOUNTING?.vehicleTypeGroup
                }
                priceType={globalFormData.MOUNTING.priceType}
              />
            )}
          </Row>
        </Card.Body>
      </Card>
      {(pricingPolicyForm.mode === 'WRITE' ||
        (pricingPolicyForm.mode === 'READ_ONLY' &&
          globalFormData.GEOMETRY_SERVICE?.hasCustomerServices)) && (
        <Card>
          <Card.Header>
            <Card.Title>
              <Title heading='xl' weight='bold'>
                <Trans i18nKey='app.common.quote.services' />
              </Title>
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <Row className='mt-3'>
              <QuoteServiceCustomServiceTable
                priceType={
                  globalFormData.MOUNTING?.priceType ??
                  PRICE_TYPE_ENUM.TAX_INCLUDED
                }
              />
            </Row>
          </Card.Body>
        </Card>
      )}
    </FormContainer>
  );
};

export default GeometryStepForm;
